import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";

import {
  generateId,
  useCollectionsData,
  useOwnerId,
  type Collection,
} from "~/state/sync";
import { mk, type MutationVars } from "~/state/mutations";
import type {
  CollectionMapKind,
  CollectionVisibility,
  GeoMapStyle,
} from "~/api/types";

export type CollectionInput = {
  name: string;
  description?: string | null;
  visibility?: CollectionVisibility;
  accentColor?: string | null;
};

export type CollectionMapInput = {
  mapKind: CollectionMapKind;
  mapStyle?: GeoMapStyle;
  centerLat?: number | null;
  centerLng?: number | null;
  zoom?: number | null;
  customMapPath?: string | null;
  customMapWidth?: number | null;
  customMapHeight?: number | null;
};

function byName(a: Collection, b: Collection) {
  if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
  return a.name.localeCompare(b.name);
}

export function useCollections() {
  const { data } = useCollectionsData();
  return useMemo(() => (data ?? []).slice().sort(byName), [data]);
}

export function useCollection(collectionId: string | undefined) {
  const { data } = useCollectionsData();
  return useMemo(
    () =>
      collectionId
        ? ((data ?? []).find((c) => c.id === collectionId) ?? null)
        : null,
    [data, collectionId],
  );
}

export function useCollectionActions() {
  const userId = useOwnerId();
  const createM = useMutation<unknown, Error, MutationVars["collectionCreate"]>(
    {
      mutationKey: [...mk.collectionCreate],
    },
  );
  const updateM = useMutation<unknown, Error, MutationVars["collectionUpdate"]>(
    {
      mutationKey: [...mk.collectionUpdate],
    },
  );
  const removeM = useMutation<unknown, Error, MutationVars["collectionRemove"]>(
    {
      mutationKey: [...mk.collectionRemove],
    },
  );

  function requireUser() {
    if (!userId) throw new Error("You must be signed in.");
    return userId;
  }

  function create(input: CollectionInput) {
    const uid = requireUser();
    const id = generateId();
    const now = new Date().toISOString();
    const row: Collection = {
      id,
      owner_id: uid,
      name: input.name.trim(),
      description: input.description?.trim() || null,
      visibility: input.visibility ?? "private",
      share_token: null,
      cover_photo_path: null,
      accent_color: input.accentColor ?? null,
      sort_order: 0,
      map_kind: "none",
      map_style: "streets",
      map_center_lat: null,
      map_center_lng: null,
      map_zoom: 18,
      custom_map_path: null,
      custom_map_width: null,
      custom_map_height: null,
      created_at: now,
      updated_at: now,
      deleted: false,
    };
    createM.mutate({ userId: uid, row });
    return id;
  }

  function update(collectionId: string, input: Partial<CollectionInput>) {
    const uid = requireUser();
    const fields: Partial<Collection> = {};
    if (input.name !== undefined) fields.name = input.name.trim();
    if (input.description !== undefined) {
      fields.description = input.description?.trim() || null;
    }
    if (input.visibility !== undefined) fields.visibility = input.visibility;
    if (input.accentColor !== undefined)
      fields.accent_color = input.accentColor;
    if (Object.keys(fields).length === 0) return;
    updateM.mutate({ userId: uid, id: collectionId, fields });
  }

  function configureMap(collectionId: string, input: CollectionMapInput) {
    const uid = requireUser();
    const fields: Partial<Collection> = { map_kind: input.mapKind };
    if (input.mapStyle !== undefined) fields.map_style = input.mapStyle;
    if (input.centerLat !== undefined) fields.map_center_lat = input.centerLat;
    if (input.centerLng !== undefined) fields.map_center_lng = input.centerLng;
    if (input.zoom !== undefined) fields.map_zoom = input.zoom;
    if (input.customMapPath !== undefined) {
      fields.custom_map_path = input.customMapPath;
    }
    if (input.customMapWidth !== undefined) {
      fields.custom_map_width = input.customMapWidth;
    }
    if (input.customMapHeight !== undefined) {
      fields.custom_map_height = input.customMapHeight;
    }
    updateM.mutate({ userId: uid, id: collectionId, fields });
  }

  function setCoverPhoto(collectionId: string, storagePath: string | null) {
    const uid = requireUser();
    updateM.mutate({
      userId: uid,
      id: collectionId,
      fields: { cover_photo_path: storagePath },
    });
  }

  function remove(collectionId: string) {
    const uid = requireUser();
    removeM.mutate({ userId: uid, id: collectionId });
  }

  return { create, update, configureMap, setCoverPhoto, remove };
}
